import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useSettings } from "@/context/SettingsContext";
import { buildPlan } from "@/lib/calculations";
import { resolveInput } from "@/lib/resolve";
import { fmtTL, fmtMonthYear } from "@/lib/format";
import { loadPlans, upsertPlan, deletePlan } from "@/lib/storage";
import { exportPlanPDF, exportPlanExcel } from "@/lib/exporters";
import { buildShareUrl, encodePlan, decodePlan } from "@/lib/share";
import { getLabels } from "@/lib/labels";
import { PdfDocument } from "@/components/PdfDocument";
import { QRCodeCanvas } from "qrcode.react";
import { PlanForm } from "@/components/calculator/PlanForm";
import { SummaryCards } from "@/components/calculator/SummaryCards";
import { PaymentTable } from "@/components/calculator/PaymentTable";
import { ScenarioCompare } from "@/components/calculator/ScenarioCompare";
import {
  Save,
  FolderOpen,
  GitCompare,
  FileDown,
  FileSpreadsheet,
  Settings,
  AlertTriangle,
  CheckCircle2,
  Copy,
  Trash2,
  Share2,
  Check,
} from "lucide-react";

const makeDefaultPlan = (settings) => {
  const today = new Date();
  const iso = today.toISOString().slice(0, 10);
  return {
    id: crypto.randomUUID(),
    name: "",
    financingAmount: 2000000,
    downPayment: 300000,
    startDate: iso,
    paymentDay: settings.defaultPaymentDay,
    termMonths: 36,
    customTerm: false,
    monthlyPayment: 100000,
    postMode: "auto",
    postMonthly: 50000,
    postRampRatio: 2,
    assetType: "ev",
    tiers: [],
    edits: {},
    additional: {},
  };
};

export default function Calculator() {
  const { settings } = useSettings();
  const [plan, setPlan] = useState(() => makeDefaultPlan(settings));
  const [savedPlans, setSavedPlans] = useState(loadPlans());
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const update = (patch) => setPlan((p) => ({ ...p, ...patch }));

  // Paylaşılan planı URL'den yükle (?p=<kod>)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("p");
    if (code) {
      const loaded = decodePlan(code);
      if (loaded) {
        setPlan(loaded);
        toast.success("Paylaşılan plan yüklendi");
      } else {
        toast.error("Geçersiz paylaşım bağlantısı");
      }
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleShare = () => {
    setShareUrl(buildShareUrl(plan));
    setCopied(false);
    setShareOpen(true);
  };

  const copyShare = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Panoya kopyalandı");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  const { rows, summary, errors } = useMemo(
    () => buildPlan(resolveInput(plan), settings),
    [plan, settings]
  );

  const labels = getLabels(plan.assetType);

  const handleSave = () => {
    const named = {
      ...plan,
      id: crypto.randomUUID(),
      name: saveName || "İsimsiz Plan",
      updatedAt: Date.now(),
    };
    setSavedPlans(upsertPlan(named));
    setPlan(named);
    setSaveOpen(false);
    setSaveName("");
    toast.success("Plan kaydedildi", { description: named.name });
  };

  const loadPlan = (p) => {
    setPlan({ ...p });
    setListOpen(false);
    toast.success("Plan yüklendi", { description: p.name });
  };
  const copyPlan = (p) => {
    const copy = { ...p, id: crypto.randomUUID(), name: `${p.name} (kopya)` };
    setSavedPlans(upsertPlan(copy));
    toast.success("Plan kopyalandı");
  };
  const removePlan = (id) => {
    setSavedPlans(deletePlan(id));
    toast.success("Plan silindi");
  };

  const handlePDF = async () => {
    toast.info("PDF oluşturuluyor...");
    try {
      await exportPlanPDF("pdf-document", `${plan.name || "berkanevim-plani"}.pdf`);
      toast.success("PDF indirildi");
    } catch (e) {
      toast.error("PDF oluşturulamadı");
    }
  };
  const handleExcel = () => {
    exportPlanExcel(rows, `${plan.name || "berkanevim-plani"}.xlsx`);
    toast.success("Excel indirildi");
  };

  return (
    <div className="min-h-screen bg-[#F7F7F8]">
      {/* Header */}
      <header className="border-b border-zinc-200/70 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/berkanevim-icon.webp"
              alt="BerkanEvim"
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl object-contain shrink-0"
              data-testid="brand-logo"
            />
            <div className="min-w-0">
              <h1 className="font-heading text-base sm:text-lg font-semibold tracking-tight leading-tight truncate">
                BerkanEvim{" "}
                <span className="text-[#1aa0dd]">| Hesaplama Aracı</span>
              </h1>
              <p className="text-xs text-zinc-500 mt-0.5 hidden sm:block">
                Ev &amp; Araba Planı Hesaplama Aracı
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            <Button variant="outline" size="sm" onClick={() => setSaveOpen(true)} data-testid="btn-kaydet" className="rounded-xl shrink-0">
              <Save size={15} className="sm:mr-1.5" />
              <span className="hidden sm:inline">Kaydet</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setSavedPlans(loadPlans()); setListOpen(true); }} data-testid="btn-planlarim" className="rounded-xl shrink-0">
              <FolderOpen size={15} className="sm:mr-1.5" />
              <span className="hidden sm:inline">Planlarım</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setSavedPlans(loadPlans()); setCompareOpen(true); }} data-testid="btn-karsilastir" className="rounded-xl shrink-0">
              <GitCompare size={15} className="sm:mr-1.5" />
              <span className="hidden sm:inline">Karşılaştır</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleShare} data-testid="btn-paylas" className="rounded-xl shrink-0">
              <Share2 size={15} className="sm:mr-1.5" />
              <span className="hidden sm:inline">Paylaş</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handlePDF} data-testid="btn-pdf" className="rounded-xl shrink-0">
              <FileDown size={15} className="sm:mr-1.5" />
              <span className="hidden sm:inline">PDF</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleExcel} data-testid="btn-excel" className="rounded-xl shrink-0">
              <FileSpreadsheet size={15} className="sm:mr-1.5" />
              <span className="hidden sm:inline">Excel</span>
            </Button>
            <Link to="/settings" className="shrink-0">
              <Button variant="ghost" size="icon" data-testid="btn-settings" className="rounded-xl">
                <Settings size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
            {labels.heading}
          </h2>
          <p className="text-zinc-500 mt-2 max-w-2xl">
            Peşinatınıza ve aylık ödemenize göre teslim tarihiniz otomatik hesaplanır.
            Teslim en erken 6. ayda ve finansmanın %{summary.deliveryTargetRate}'i
            ödendiğinde gerçekleşir.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sol kolon */}
          <div className="lg:col-span-4">
            <PlanForm plan={plan} update={update} summary={summary} />
          </div>

          {/* Sağ kolon */}
          <div className="lg:col-span-8 space-y-6" id="printable-plan">
            {errors.length > 0 && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 space-y-1" data-testid="error-box">
                {errors.map((e, i) => (
                  <p key={i} className="text-sm text-red-700 flex items-center gap-2">
                    <AlertTriangle size={15} /> {e}
                  </p>
                ))}
              </div>
            )}

            {errors.length === 0 && (
              <>
                {/* Teslim tarihi otomatik sonucu */}
                {summary.deliveryAchievable ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="delivery-result">
                    <p className="text-sm text-emerald-800 font-medium flex items-center gap-2">
                      <CheckCircle2 size={16} /> Girdiğiniz ödeme planıyla %
                      {summary.deliveryTargetRate} teslim şartı{" "}
                      <span className="font-semibold">{summary.preDeliveryMonths}. ayda</span>{" "}
                      ({fmtMonthYear(summary.deliveryDate)}) karşılanıyor.
                    </p>
                    <p className="text-xs text-emerald-700 mt-1">
                      Teslimde toplam ödenmiş: {fmtTL(summary.deliveryCumulative)} · Gerekli
                      minimum: {fmtTL(summary.deliveryTargetAmount)} · Fazla:{" "}
                      {fmtTL(Math.max(0, summary.deliveryCumulative - summary.deliveryTargetAmount))}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="delivery-fail">
                    <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                      <AlertTriangle size={16} /> Girdiğiniz aylık ödeme ile toplam vade (
                      {summary.termMonths} ay) içinde %{summary.deliveryTargetRate} teslim
                      şartına ulaşılamıyor.
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      {summary.minDeliveryMonths}. ayda teslim için gerekli minimum aylık ödeme:{" "}
                      {fmtTL(summary.minimumMonthly)}
                    </p>
                  </div>
                )}

                {summary.paymentRatioWarning && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="ratio-warning">
                    <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
                      <AlertTriangle size={16} /> Aylık ödemeler arasındaki fark en fazla 3
                      kat olabilir.
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      En yüksek aylık ödeme ({fmtTL(summary.paymentRatioWarning.max)}), en
                      düşük aylık ödemenin ({fmtTL(summary.paymentRatioWarning.min)}){" "}
                      {summary.paymentRatioWarning.ratio.toLocaleString("tr-TR", {
                        maximumFractionDigits: 1,
                      })}{" "}
                      katı. Lütfen teslim öncesi/sonrası aylık ödemeleri birbirine
                      yaklaştırın.
                    </p>
                  </div>
                )}

                <SummaryCards summary={summary} labels={labels} />
                <PaymentTable rows={rows} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Kaydet Dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Planı Kaydet</DialogTitle>
            <DialogDescription>Planınıza bir isim verin.</DialogDescription>
          </DialogHeader>
          <Input
            data-testid="input-plan-name"
            placeholder="Örn: 2M - Mart 2027 Teslim"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            className="rounded-xl h-11"
          />
          <DialogFooter>
            <Button onClick={handleSave} data-testid="btn-save-confirm" className="rounded-xl bg-zinc-900 w-full">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Planlarım Dialog */}
      <Dialog open={listOpen} onOpenChange={setListOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Kayıtlı Planlarım</DialogTitle>
            <DialogDescription>Kayıtlı planlarınızı açın, kopyalayın veya silin.</DialogDescription>
          </DialogHeader>
          {savedPlans.length === 0 ? (
            <p className="text-sm text-zinc-500 py-6 text-center">Henüz kayıtlı plan yok.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {savedPlans.map((p) => (
                <div key={p.id} className="flex items-center justify-between border border-zinc-200 rounded-xl p-3" data-testid={`saved-plan-${p.id}`}>
                  <div>
                    <p className="font-medium text-sm">{p.name}</p>
                    <p className="text-xs text-zinc-400">{fmtTL(p.financingAmount)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => loadPlan(p)} className="rounded-lg h-8 text-xs">Aç</Button>
                    <Button size="icon" variant="ghost" onClick={() => copyPlan(p)} className="h-8 w-8"><Copy size={15} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => removePlan(p.id)} className="h-8 w-8 text-red-500"><Trash2 size={15} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ScenarioCompare
        open={compareOpen}
        onOpenChange={setCompareOpen}
        savedPlans={savedPlans}
        settings={settings}
      />

      {errors.length === 0 && (
        <PdfDocument plan={plan} summary={summary} rows={rows} labels={labels} />
      )}

      {/* Paylaş Dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Planı Paylaş</DialogTitle>
            <DialogDescription>Bağlantı veya QR kod ile planınızı paylaşın.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-zinc-500">
            Aşağıdaki bağlantıyı paylaşın. Bağlantıyı açan kişi planı aynen görüntüler.
          </p>
          {shareUrl && (
            <div className="flex justify-center py-2">
              <div className="p-3 bg-white rounded-xl border border-zinc-200" data-testid="share-qr">
                <QRCodeCanvas value={shareUrl} size={160} level="M" />
              </div>
            </div>
          )}
          <p className="text-xs text-zinc-400 text-center -mt-1">
            Telefonunuzun kamerasıyla QR kodu okutarak planı hızlıca açabilirsiniz.
          </p>
          <div className="space-y-2">
            <span className="text-[11px] uppercase tracking-[0.1em] text-zinc-500 font-medium">
              Paylaşım Bağlantısı
            </span>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                data-testid="share-url"
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="rounded-xl h-11 text-xs bg-zinc-50 tabular-nums"
              />
              <Button
                size="icon"
                data-testid="btn-copy-share"
                onClick={() => copyShare(shareUrl)}
                className="rounded-xl h-11 w-11 shrink-0 bg-zinc-900"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <span className="text-[11px] uppercase tracking-[0.1em] text-zinc-500 font-medium">
              Paylaşım Kodu
            </span>
            <textarea
              readOnly
              data-testid="share-code"
              value={encodePlan(plan)}
              onFocus={(e) => e.target.select()}
              rows={3}
              className="w-full rounded-xl bg-zinc-50 border border-zinc-200 p-3 text-xs break-all resize-none focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
